import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Query Source master (Masters → Query Sources). Where a lead originated
 * (e.g. "website", "referral", "walk_in"). A pure simple master - see ADR-0016
 * / ADR-0022 for the standard shape. Parent of Query Sub Sources (ADR-0024).
 */

export const QuerySourceListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type QuerySourceListItem = z.infer<typeof QuerySourceListItemSchema>;

export const QuerySourceListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type QuerySourceListQuery = z.infer<typeof QuerySourceListQuerySchema>;

export const QuerySourceListResponseSchema = paginatedResponseSchema(QuerySourceListItemSchema);
export type QuerySourceListResponse = z.infer<typeof QuerySourceListResponseSchema>;

export const QuerySourceDetailSchema = QuerySourceListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type QuerySourceDetail = z.infer<typeof QuerySourceDetailSchema>;

const QuerySourceCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateQuerySourceRequestSchema = QuerySourceCoreFieldsSchema;
export type CreateQuerySourceRequest = z.infer<typeof CreateQuerySourceRequestSchema>;

export const UpdateQuerySourceRequestSchema = QuerySourceCoreFieldsSchema.partial();
export type UpdateQuerySourceRequest = z.infer<typeof UpdateQuerySourceRequestSchema>;

// ---------------------------------------------------------------------------
// Options (slim dropdown shape for the Sub Source parent picker)
// ---------------------------------------------------------------------------

export const QuerySourceOptionSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});
export type QuerySourceOption = z.infer<typeof QuerySourceOptionSchema>;

export const QuerySourceOptionListSchema = z.array(QuerySourceOptionSchema);
export type QuerySourceOptionList = z.infer<typeof QuerySourceOptionListSchema>;

export const QuerySourceOptionsQuerySchema = z.object({
  q: z.string().trim().min(1).max(120).optional(),
  activeOnly: BooleanQueryParamSchema.optional(),
});
export type QuerySourceOptionsQuery = z.infer<typeof QuerySourceOptionsQuerySchema>;
